import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import ApiService from '../services/api.service';
import { invalidateThaklisServicesCache } from '../hooks/useThaklisServicesOff';
import { isPlatformLevel, can } from '../utils/roleUtils';

/**
 * Work Order settings (per typing center): whether Thaklis services are offered in the WO service
 * picker, whether WO tasks auto-complete when their backing work completes, and whether a work order
 * auto-completes when its last task completes. Mirrors InvoiceConfig.
 */
const WorkOrderConfig = ({ currentUser }) => {
  const isPlatform = isPlatformLevel(currentUser?.role);
  const canEdit = can(currentUser, 'settings', 'write');

  const [typingCenters, setTypingCenters] = useState([]);
  const [selectedTypingCenter, setSelectedTypingCenter] = useState('');
  const [config, setConfig] = useState({
    thaklis_services_enabled: false,
    auto_complete_work_order_task: true,
    auto_complete_work_order: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Resolve the target TC: platform users pick one; TC users use their own.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isPlatform) {
          const resp = await ApiService.getTypingCenters();
          const list = resp?.data?.data || resp?.data || [];
          if (!cancelled) {
            setTypingCenters(list);
            if (list.length && !selectedTypingCenter) setSelectedTypingCenter(list[0].id);
          }
        } else if (currentUser?.typingCenterId) {
          if (!cancelled) setSelectedTypingCenter(currentUser.typingCenterId);
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load typing centers');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isPlatform]);

  const loadConfig = useCallback(async (tcId) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await ApiService.getWorkOrderConfig(tcId);
      const c = resp?.data?.data || resp?.data || {};
      setConfig({
        thaklis_services_enabled: c.thaklis_services_enabled === true,
        // Auto-complete defaults on: only an explicit false is off.
        auto_complete_work_order_task: c.auto_complete_work_order_task !== false,
        auto_complete_work_order: c.auto_complete_work_order === true,
      });
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to load work order settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTypingCenter) loadConfig(selectedTypingCenter);
  }, [selectedTypingCenter, loadConfig]);

  const handleSave = async () => {
    if (!selectedTypingCenter) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.setWorkOrderConfig(selectedTypingCenter, config);
      invalidateThaklisServicesCache();
      setSuccess('Work order settings saved');
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to save work order settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 760 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Work Order Settings</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Control how work orders behave for this typing center.
      </Typography>

      {isPlatform && (
        <FormControl size="small" sx={{ mb: 3, minWidth: 280 }}>
          <InputLabel>Typing Center</InputLabel>
          <Select
            label="Typing Center"
            value={selectedTypingCenter}
            onChange={(e) => setSelectedTypingCenter(e.target.value)}
          >
            {typingCenters.map((tc) => (
              <MenuItem key={tc.id} value={tc.id}>{tc.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>You have view-only access to these settings.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={config.thaklis_services_enabled}
                disabled={!canEdit}
                onChange={(e) => setConfig((c) => ({ ...c, thaklis_services_enabled: e.target.checked }))}
              />
            }
            label={<Typography sx={{ fontWeight: 600 }}>Enable Thaklis services</Typography>}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: 6, mb: 1 }}>
            When on, Thaklis catalog services appear in a secondary tab (behind a warning) in the
            work-order service picker, on both the TC and portal create/detail pages, and the
            Tickets, Create Ticket and Thaklis Payments menus, dashboard Thaklis widgets and
            invoice-from-tickets actions are shown. When off, all of these are hidden and Thaklis
            services cannot be added to work orders.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={config.auto_complete_work_order_task}
                disabled={!canEdit}
                onChange={(e) => setConfig((c) => ({ ...c, auto_complete_work_order_task: e.target.checked }))}
              />
            }
            label={<Typography sx={{ fontWeight: 600 }}>Auto-complete work order tasks</Typography>}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: 6, mb: 1 }}>
            When on, a work-order task is completed automatically once the work backing it completes —
            its linked internal task, or the work order handed to a B2B provider. Tasks linked to a
            Thaklis ticket are not affected.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={config.auto_complete_work_order}
                onChange={(e) => setConfig((c) => ({ ...c, auto_complete_work_order: e.target.checked }))}
              />
            }
            label={<Typography sx={{ fontWeight: 600 }}>Auto-complete work orders</Typography>}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: 6, mb: 1 }}>
            When on, a work order is completed automatically the moment its last task is completed,
            exactly as if Complete had been clicked. Work orders on hold or without tasks are not
            affected. Off by default, which keeps the explicit Complete step for review.
          </Typography>

          {canEdit && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !selectedTypingCenter}
                startIcon={saving ? <CircularProgress size={18} /> : null}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default WorkOrderConfig;
